/**
 * @fileoverview Module for `src/dashboard/manage-letters/common/ManageColumnCell.js`.
 */

import moment from 'moment';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import CampaignLabels from '../../../campaigns/CampaignLabels';
import MailDate from '../../../MailDate';

ManageColumnCell.propTypes = {
  bundleData: PropTypes.object.isRequired,
  canAdoptSocial: PropTypes.bool.isRequired,
  cellId: PropTypes.string.isRequired,
  children: PropTypes.any,
  isReady: PropTypes.bool.isRequired,
  testLabel: PropTypes.string.isRequired,
};

/**
 * Derives the party bundle ownership label shown in social bundle cards.
 *
 * @param {Object} bundle
 * @returns {string} Ownership label for party letters.
 */
const getPartyLetterLabel = (bundle) => {
  if (bundle.social_assignee_email && bundle.email === bundle.social_assignee_email) {
    return 'MY';
  }
  if (bundle.social_assignee_first_name) {
    return `${ bundle.social_assignee_first_name }'S`.toUpperCase();
  }
  return 'UNCLAIMED';
};

/**
 * Shared cell wrapper for manage letters bundle cards.
 *
 * @param {Object} params
 * @returns {JSX.Element} Bundle metadata, labels, and child controls.
 */
export default function ManageColumnCell({
  children,
  cellId,
  bundleData,
  testLabel,
  canAdoptSocial,
  isReady,
}) {
  const mailDateFrom = (bundleData.mail_date_type === 'ROLLING' || bundleData.mail_date_type === 'ROLLING_ASAP')
    ? bundleData.mail_start_date
    : bundleData.mail_end_date;
  const cellType = bundleData.social_token ? 'party' : 'individual';
  const cellLabel = bundleData.social_token
    ? `EVENT LETTER CAMPAIGN - ${ getPartyLetterLabel(bundleData) } LETTERS`
    : 'INDIVIDUAL LETTER CAMPAIGN';

  let campaignLabelCount = 1;
  if (bundleData.is_priority) {
    campaignLabelCount += 1;
  }
  if (bundleData.is_labs) {
    campaignLabelCount += 1;
  }

  const isFlyerCampaign = ['CA_2025_C4_PROP50_FCOL', 'CA_2025_C4_PROP50_FBW', 'CA49-2040'].includes(bundleData.district_id);
  return (
    <li className={ `bundle-list-item ${ cellType } ${ isReady ? '' : 'not-ready' }` } data-testid={ testLabel } key={ cellId } >
      { canAdoptSocial && (<p className='fill-row cell-label'>{ cellLabel }</p>) }
      <div className='row'>
        <div className={ `fill-auto campaign-labels-${ campaignLabelCount }` }>
          <h4 className='fill-row bundle-cell-contents'>{ bundleData.short_description }</h4>
        </div>
        <div className='col-auto'>
          <CampaignLabels
            isBundleDisplay
            isLabs={ bundleData.is_labs }
            isPolitical={ bundleData.tax_type === 'political' }
            isPriority={ bundleData.is_priority }
            isSocial={ bundleData.tax_type === 'social' }
            isVoterRegistration={ bundleData.tax_type === 'voter-engagement' }
          />
        </div>
      </div>
      <div className='row'>
        <div className='col fill-row bundle-cell-contents'>
          <span className='adopted-on bundle-cell-contents'>
            ID: <span className='font-monospace bundle-cell-contents'>{ ` ${ cellId }` }</span>
            { ` | ${ bundleData.letter_kit_status === 'REQUESTED' ? 'Requested' : 'Adopted' }: ${ moment(bundleData.bundle_adopted_at).format('M/DD [at] hh:ssa') }` }
          </span>
        </div>
      </div>
      <div className='row'>
        <div className='col fill-row fw-bold mail-by-message bundle-cell-contents'>
          <div className='mail-by-bubble'>
            <MailDate iconClassname={ isFlyerCampaign ? 'fa-print' : 'fa-envelope-o' } isFlyer={ isFlyerCampaign } mailDate={ bundleData.mail_end_date } mailDateFrom={ mailDateFrom } mailDateType={ bundleData.mail_date_type } short showOffset={ false } />
          </div>
          <div
            className={ classNames(
              'mail-by-label rounded-pill mail-by-bubble-label', 'text-gray',
            ) }
          >
            <MailDate iconClassname='' isFlyer={ isFlyerCampaign } mailDate={ bundleData.mail_end_date } mailDateFrom={ mailDateFrom } mailDateType={ bundleData.mail_date_type } short={ false } />
          </div>
        </div>
      </div>
      { children }
    </li>
  );
}
